import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { locales, defaultLocale } from '@/locales';
import { logger } from '@/utils/logger';

// 支持的语言列表
const SUPPORTED_LANGUAGES = ['zh-CN', 'zh-TW', 'en'] as const;
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// 本地存储键名
const LANGUAGE_STORAGE_KEY = 'changmei-language';

// 从localStorage获取保存的语言设置
const getStoredLanguage = (): string => {
  if (typeof window === 'undefined') return defaultLocale;
  
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
      return stored;
    }
  } catch (error) {
    logger.warn('Failed to read language from localStorage', error);
  }
  return defaultLocale;
};

// 获取浏览器语言偏好
const getBrowserLanguage = (): string => {
  if (typeof window === 'undefined') return defaultLocale;
  
  try {
    const browserLang = navigator.language || navigator.languages?.[0];
    
    // 精确匹配
    if (browserLang && SUPPORTED_LANGUAGES.includes(browserLang as SupportedLanguage)) {
      return browserLang;
    }
    
    // 语言代码匹配（如 zh -> zh-CN）
    const langCode = browserLang?.split('-')[0];
    if (langCode === 'zh') {
      // 根据地区判断简繁体
      const region = browserLang.split('-')[1]?.toUpperCase();
      if (['TW', 'HK', 'MO'].includes(region)) {
        return 'zh-TW';
      }
      return 'zh-CN';
    }
    
    if (langCode === 'en') {
      return 'en';
    }
  } catch (error) {
    logger.warn('Failed to detect browser language', error);
  }
  
  return defaultLocale;
};

// 初始化 i18next
i18n
  .use(initReactI18next)
  .init({
    resources: {
      'zh-CN': {
        translation: locales['zh-CN']
      },
      'zh-TW': {
        translation: locales['zh-TW']
      },
      'en': {
        translation: locales['en']
      }
    },
    lng: getStoredLanguage() || getBrowserLanguage(),
    fallbackLng: defaultLocale,
    
    // 插值配置
    interpolation: {
      escapeValue: false, // React 已经处理了 XSS 防护
    },
    
    // React 配置
    react: {
      useSuspense: false, // 避免 SSR 问题
      bindI18n: 'languageChanged',
      bindI18nStore: '',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'em']
    },
    
    // 性能优化
    load: 'all', // 加载完整的语言代码（包括地区）
    preload: SUPPORTED_LANGUAGES, // 预加载所有支持的语言
    
    // 调试配置
    debug: import.meta.env.DEV,
    
    // 缓存配置
    cache: {
      enabled: true,
      prefix: 'changmei_i18n_',
      expirationTime: 7 * 24 * 60 * 60 * 1000, // 7天
      versions: {
        'zh-CN': '1.1',
        'zh-TW': '1.1',
        'en': '1.1'
      }
    },
    
    // 错误处理
    saveMissing: false, // 关闭缺失翻译警告
    missingKeyHandler: false
  });

// 监听语言变化并保存到localStorage
i18n.on('languageChanged', (lng) => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    document.documentElement.lang = lng;
    
    // 更新页面方向（如果需要支持 RTL 语言）
    document.documentElement.dir = 'ltr'; // 当前支持的语言都是 LTR
    
    // 触发自定义事件，通知其他组件语言已变化
    window.dispatchEvent(new CustomEvent('languageChanged', { 
      detail: { language: lng } 
    }));
  } catch (error) {
    logger.warn('Failed to save language to localStorage', error);
  }
});

// 错误处理
i18n.on('failedLoading', (lng, _ns, msg) => {
  logger.error(`Failed to load language ${lng}`, msg);
});

// 导出工具函数
export const changeLanguage = (language: string) => {
  if (SUPPORTED_LANGUAGES.includes(language as SupportedLanguage)) {
    return i18n.changeLanguage(language);
  }
  logger.warn(`Unsupported language: ${language}`);
  return Promise.resolve();
};

export const getCurrentLanguage = () => i18n.language;

export const getSupportedLanguages = () => [...SUPPORTED_LANGUAGES];

export default i18n;